import { requestJson } from "@/lib/api";
import type { StaffMember, StaffRole } from "@/lib/types";

interface StaffDto {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
  status: string;
  createdAt: string;
  commissionPercent?: number | null;
  permissions?: string[];
}

function toStaffMember(dto: StaffDto): StaffMember {
  return {
    id: dto.id,
    email: dto.email,
    name: dto.name,
    role: dto.role,
    status: dto.status as StaffMember["status"],
    createdAt: dto.createdAt,
    commissionPercent: dto.commissionPercent ?? null,
    permissions: dto.permissions ?? [],
  };
}

export async function fetchStaff(): Promise<StaffMember[]> {
  const list = await requestJson<StaffDto[]>("/api/v1/admin/staff");
  return list.map(toStaffMember);
}

export async function createStaff(input: {
  email: string;
  name: string;
  role: StaffRole;
  password: string;
  commissionPercent?: number | null;
  /** Acessos escolhidos na tela (obrigatório quando a personalização está habilitada). */
  permissions?: string[];
}): Promise<StaffMember> {
  const dto = await requestJson<StaffDto>("/api/v1/admin/staff", {
    method: "POST",
    body: input,
  });
  return toStaffMember(dto);
}

export async function updateStaff(
  id: string,
  input: { email: string; name: string; role: StaffRole; password?: string; commissionPercent?: number | null },
): Promise<StaffMember> {
  const dto = await requestJson<StaffDto>(`/api/v1/admin/staff/${id}`, {
    method: "PATCH",
    body: input,
  });
  return toStaffMember(dto);
}

export async function deactivateStaff(id: string): Promise<void> {
  await requestJson<void>(`/api/v1/admin/staff/${id}`, { method: "DELETE" });
}

// ---- Permissões (catálogo + modelo + edição de acessos por usuário) ----

/** Uma permissão do catálogo (Módulo → Submódulo → Ação) com dependência e flags. */
export interface PermissionCatalogEntry {
  code: string;
  module: string;
  submodule: string;
  action: string;
  dependsOn: string | null;
  restricted: boolean;
  valueBearing: boolean;
}

export function fetchPermissionCatalog(): Promise<PermissionCatalogEntry[]> {
  return requestJson<PermissionCatalogEntry[]>("/api/v1/admin/staff/permission-catalog");
}

/** Modelo (sugestão) de acessos de um cargo — a tela carrega e envia explicitamente. */
export function fetchPermissionTemplate(role: StaffRole): Promise<string[]> {
  return requestJson<string[]>(`/api/v1/admin/staff/permission-templates/${role}`);
}

/** Substitui integralmente os acessos de um funcionário. */
export async function updateStaffPermissions(
  id: string,
  permissions: string[],
  reason?: string,
): Promise<StaffMember> {
  const dto = await requestJson<StaffDto>(`/api/v1/admin/staff/${id}/permissions`, {
    method: "PUT",
    body: { permissions, reason },
  });
  return toStaffMember(dto);
}
